import { Redux } from '../..'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { GygEonComment, GygEonCommentsQueryOptions } from '@/src/types/apps/gygEonComments'

interface EonCommentsInitialState {
  comments: GygEonComment[]
  loading: boolean
  success: string | null
  error: string | null
}

const initialState: EonCommentsInitialState = {
  comments: [],
  loading: false,
  success: null,
  error: null
}

interface EonCommentsResponse {
  data: GygEonComment[]
}

interface GygEonCommentsPayload {
  restaurantId: number
  date: string
  labourComment: string
  shiftLeaderComment: string
  shiftLeaderName: string
  dayPart: string
}

// Flexible payload supporting role-based variations (CoC vs RM/Shift Leader)
// type GygEonCommentsPayload = GygEonCommentsSavePayload

export const getEonCommentsInTwoDates = createAsyncThunk<EonCommentsResponse, GygEonCommentsQueryOptions, Redux>(
  'gygEonComments/getEonCommentsInTwoDates',
  async (gygEonCommentsQueryOptions, { extra }) => {
    const { restaurantId, startDate, endDate, take } = gygEonCommentsQueryOptions

    const { data: responseData } = await extra.httpClient.get(`restaurants/${restaurantId}/eon-comments`, {
      params: {
        startDate,
        endDate,
        take
      }
    })

    return responseData
  }
)

export const saveEonComments = createAsyncThunk<GygEonComment, GygEonCommentsPayload, Redux>(
  'gygEonComments/saveEonComments',
  async (gygEonComments, { extra }) => {
    const { restaurantId, ...rest } = gygEonComments
    const { data: responseData } = await extra.httpClient.post(`restaurants/${restaurantId}/eon-comments`, {
      ...rest
    })

    return responseData
  }
)

const gygEonCommentsSlice = createSlice({
  name: 'getAllEonComments',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getEonCommentsInTwoDates.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(getEonCommentsInTwoDates.fulfilled, (state, action) => {
        state.loading = false
        state.success = null
        state.comments = action.payload.data
      })
      .addCase(getEonCommentsInTwoDates.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to fetch eon goals targets'
      })
      .addCase(saveEonComments.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(saveEonComments.fulfilled, state => {
        state.loading = false
        state.success = 'Eon comments saved successfully'
        state.error = null
      })
      .addCase(saveEonComments.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to save eon comments'
      })
  }
})

export default gygEonCommentsSlice.reducer
